import { useParams } from "react-router";
import { Grid, Link } from "@mui/material";

import {
  FormDateField,
  FormPolicyholderOrganizationRelationshipSelect,
  FormPolicyholderOrganizationTypeSelect,
  FormPrimaryInsuredTypeSelect,
  FormTextField,
} from "@covertree/ui";
import { InfoDisclaimerBox } from "@covertree/ui/content";

import { SectionCard } from "../../../SectionCard";
import { AddSomeoneElseInline } from "./AddSomeoneElseInline";
import { usePersonalDetailsAndAdditionalInsured } from "./usePersonalDetailsAndAdditionalInsured";

interface PersonalDetailsAndAdditionalInsuredProps {
  viewOnly?: boolean;
}

/** Read-only insured details plus the inline "add someone else" control. */
export const PersonalDetailsAndAdditionalInsured = ({
  viewOnly = false,
}: PersonalDetailsAndAdditionalInsuredProps) => {
  const { policyLocator = "" } = useParams();
  const {
    control,
    isOrganization,
    showDescribeOrgType,
    firstPersonName,
    additionalInsured,
    additionalPersonDetails,
    handleAdditionalInsuredFormSubmit,
    handleToggleAdditionalPersonSwitch,
  } = usePersonalDetailsAndAdditionalInsured();

  return (
    <SectionCard
      title="Personal Details And Additional Insured"
      dataTestId="personal-details-accordion"
      fieldNames={[
        "type",
        "firstName",
        "middleName",
        "lastName",
        "insuredDateOfBirth",
        "orgName",
        "orgType",
        "describeOrgType",
        "repRelationToOrg",
      ]}>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <FormPrimaryInsuredTypeSelect
            control={control}
            fontSize={18}
            labelFontSize={16}
            disabled
          />
        </Grid>

        {/* Organization-only header fields, mirroring the new-business flow. */}
        {isOrganization && (
          <>
            <Grid item xs={12} sm={6}>
              <FormTextField
                id="organization-name"
                name="orgName"
                label="Organization name"
                control={control}
                fontSize={18}
                labelFontSize={16}
                dataTestId="organization-name"
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormPolicyholderOrganizationTypeSelect
                control={control}
                fontSize={18}
                labelFontSize={16}
                disabled
              />
            </Grid>

            {showDescribeOrgType && (
              <Grid item xs={12}>
                <FormTextField
                  id="describe-org-type"
                  name="describeOrgType"
                  label="Describe organization type"
                  control={control}
                  fontSize={18}
                  labelFontSize={16}
                  dataTestId="describe-org-type"
                  disabled
                />
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12} sm={6}>
          <FormTextField
            id="first-name"
            name="firstName"
            label={isOrganization ? "Representative first name" : "First name"}
            control={control}
            fontSize={18}
            labelFontSize={16}
            dataTestId="first-name"
            disabled={isOrganization ? viewOnly : true}
          />
        </Grid>

        {!isOrganization && (
          <Grid item xs={12} sm={6}>
            <FormTextField
              id="middle-name"
              name="middleName"
              label="Middle name (optional)"
              control={control}
              fontSize={18}
              labelFontSize={16}
              dataTestId="middle-name"
              disabled
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormTextField
            id="last-name"
            name="lastName"
            label={isOrganization ? "Representative last name" : "Last name"}
            control={control}
            fontSize={18}
            labelFontSize={16}
            dataTestId="last-name"
            disabled={isOrganization ? viewOnly : true}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormDateField
            id="date-of-birth"
            name="insuredDateOfBirth"
            label={
              isOrganization ? "Representative date of birth" : "Date of birth"
            }
            control={control}
            fontSize={18}
            labelFontSize={16}
            dataTestId="date-of-birth"
            disabled={isOrganization ? viewOnly : true}
          />
        </Grid>

        {isOrganization && (
          <Grid item xs={12} sm={6}>
            <FormPolicyholderOrganizationRelationshipSelect
              control={control}
              fontSize={18}
              labelFontSize={16}
              disabled
            />
          </Grid>
        )}

        <Grid item xs={12} mt={4} mb={4}>
          <InfoDisclaimerBox fontSize={14} data-testid="contact-info-box">
            Need to update mailing address, phone, or email? These can be
            changed from{" "}
            <Link
              rel="noopener noreferrer"
              color="secondary"
              href={`/policies/${policyLocator}?tab=personalDetails`}
              target="_blank">
              {" "}
              Policy details
            </Link>
            .
          </InfoDisclaimerBox>
        </Grid>

        <Grid item xs={12}>
          <AddSomeoneElseInline
            firstPersonName={firstPersonName}
            additionalInsured={additionalInsured}
            subheader={additionalPersonDetails}
            disabled={viewOnly}
            onSubmit={handleAdditionalInsuredFormSubmit}
            onRemove={handleToggleAdditionalPersonSwitch}
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};
